import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { ITourPackage } from '@appTypes/redefined-types';
import { TranslatePipe } from '@ngx-translate/core';
import { FlightLegListComponent } from '@shared/components/flight-leg-list/flight-leg-list.component';
import { StarsComponent } from '@shared/components/stars/stars.component';
import { PriceFormatPipe } from '@shared/pipes/price-format.pipe';
import { calendarDaysBetween, tourStayLabel } from '@pages/tours-page/utils/tour-stay-label';
import { cartFareLine } from '@pages/tours-page/utils/tour-fares';
import { environment } from '@env/environment';

/**
 * The tour in the cart (docs flows/tour-package.md R-TD7 / R-TD10, features/website/cart.md R-C31 .. R-C34; Amiran 09.10.2026 OQ-61 / OQ-64): every outbound
 * and return leg on its own line (app-flight-leg-list - never the first leg's landing as "the arrival"), the trip (first take-off .. first return take-off) and
 * the hotel stay with "N of M nights" when the flight lands 2+ days after take-off, plus the "Evening before" chip when the stay starts before the landing day.
 */
@Component({
  selector: 'app-cart-tour-item-view',
  templateUrl: './cart-tour-item-view.component.html',
  styleUrl: './cart-tour-item-view.component.scss',
  imports: [StarsComponent, MatIcon, DatePipe, PriceFormatPipe, TranslatePipe, FlightLegListComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CartTourItemViewComponent {
  readonly data = input<ITourPackage>();
  /** R-TB12: the fare the agent chose in Details (the Air Arabia bundle name, or the Fly Dubai / My Agent family) - the cart item's own field. */
  readonly selectedFareCode = input<string | null | undefined>(null);

  /** R-TB12: "Fare: VALUE" / "Bundle: Ultimate"; null for a tour with no chosen fare. */
  readonly fareLine = computed(() => cartFareLine(this.data()?.fares, this.selectedFareCode()));

  readonly dateFormat = environment.formats.date;
  readonly dateTimeFormat = environment.formats.dateTime;

  /** The trip: first take-off .. first return take-off, and its nights (the nights the agent searched). */
  readonly trip = computed(() => {
    const flight = this.data()?.flight;
    const from = flight?.departureSegments?.at(0)?.departureFlightDate ?? flight?.departureDate;
    const to = flight?.returnSegments?.at(0)?.departureFlightDate ?? flight?.returnDate;
    const nights = calendarDaysBetween(from, to);
    return from && to && nights !== null ? { from, to, nights } : null;
  });

  /** Landing-day / evening-before stay (R-TD7 / R-TD8); null when the hotel starts on the take-off day. */
  readonly stay = computed(() => tourStayLabel(this.data()));
}
